import { injectable } from 'tsyringe';
import BaseStoreService from '../BaseStoreService';
import IStoreService from '../IStoreService';
import storeDefaultState from '../../../interface/v1/store/storeDefaultState';
import * as T from '../types';
import { ServiceStatus } from '../../../core/services/types';

@injectable()
export default class WebStoreService
  extends BaseStoreService
  implements IStoreService
{
  constructor() {
    super();
  }

  async init(): Promise<void> {
    this._status = ServiceStatus.INITIALIZING;

    this._state = { ...storeDefaultState };
    this._status = ServiceStatus.READY;
    this._resolveIsReady(true);
  }

  setState(
    data: T.StoreSetStateDataType,
    options?: T.StoreSetStateOptionsType
  ): void {
    const { deepSafeSetValue = true } = options || {};
    const newValue = typeof data === 'function' ? data(this._state) : data;
    const previousState = { ...this._state };

    const isIterableObject = (data: any) =>
      Object.prototype.toString.call(data) === '[object Object]';

    if (isIterableObject(newValue)) {
      if (deepSafeSetValue) {
        const safeSetValue = (oldData: any, newData: any) => {
          for (const key in newData) {
            const newKeyValue = newData[key];

            if (newKeyValue === undefined) {
              delete oldData[key];
            } else if (isIterableObject(newKeyValue)) {
              if (!isIterableObject(oldData[key])) {
                oldData[key] = newKeyValue;
              } else {
                safeSetValue(oldData[key], newKeyValue);
              }
            } else {
              oldData[key] = newKeyValue;
            }
          }
        };
        safeSetValue(this._state, newValue);
      } else {
        const newState = { ...this._state, ...newValue };
        for (const key in newState) {
          if (newState[key] === undefined) {
            delete newState[key];
          }
        }
        this._state = newState;
      }
    }

    this._notifyObservers(previousState);
  }

  dangerousSetState(data: T.StoreSetStateDataType): void {
    const newValue = typeof data === 'function' ? data(this._state) : data;
    const previousState = { ...this._state };
    this._state = newValue;
    this._notifyObservers(previousState);
  }

  addObserver(id: string, callback: T.StoreObserverCallbackType): void {
    this._observers.set(id, callback);
  }

  removeObserver(id: string): void {
    this._observers.delete(id);
  }

  generateId(): string {
    this._lastGeneratedId += 1;
    return String(this._lastGeneratedId);
  }

  useReactStore(React: any): any {
    const methods = {
      useEffect: React?.useEffect,
      useState: React?.useState
    };

    if (Object.values(methods).some((method) => typeof method !== 'function')) {
      return undefined;
    }

    const [updateCount, setUpdateCount] = methods.useState(0);

    methods.useEffect(() => {
      const thisId = this.generateId();
      this._internalObservers.set(thisId, () =>
        setUpdateCount((prev: number) => prev + 1)
      );

      return () => {
        this._internalObservers.delete(thisId);
      };
    }, []);

    return {
      updateCount,
      state: this._state,
      setState: this.setState.bind(this),
      dangerousSetState: this.dangerousSetState.bind(this),
      addObserver: this.addObserver.bind(this),
      removeObserver: this.removeObserver.bind(this),
      generateId: this.generateId.bind(this),
      useReactStore: this.useReactStore.bind(this)
    };
  }
}
